/**
 * Browser boundary for the visitor ID scanner and badge printer.
 *
 * Scanning still has no Mac SDK. A scanner driver does not give this page
 * the scanner. An optional window.CarPoolIdScanner can be attached later.
 *
 * Badge printing runs in this browser, on the computer where the LabelWriter
 * is plugged in. See dymoBadgePrinter.js. window.CarPoolBadgePrinter remains
 * an optional override and is not a server-side print path.
 */

import { printDymoVisitorBadge } from './dymoBadgePrinter';
import { idScannerIsReady, scanPreparedId, SCANNER_UNAVAILABLE_MESSAGE } from './idScanner';

function clean(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  return text || '';
}

export function readScanFields(raw) {
  if (!raw || typeof raw !== 'object') {
    return { fullName: '', documentToken: null, imageDataUrl: null };
  }

  const first = clean(raw.first_name || raw.firstName);
  const last = clean(raw.last_name || raw.lastName);
  const fullName = clean(raw.full_name || raw.fullName) || [first, last].filter(Boolean).join(' ');
  const documentToken = clean(raw.document_token || raw.documentToken) || null;
  const imageDataUrl = typeof raw.image_data_url === 'string' && raw.image_data_url.startsWith('data:image/')
    ? raw.image_data_url
    : null;

  return {
    fullName,
    firstName: first,
    middleName: clean(raw.middle_name || raw.middleName),
    lastName: last,
    suffix: clean(raw.suffix),
    birthDate: clean(raw.birth_date || raw.birthDate || raw.date_of_birth || raw.dateOfBirth),
    documentToken,
    imageDataUrl,
  };
}

export function localHardwareStatus() {
  const scanner = typeof window !== 'undefined' ? window.CarPoolIdScanner : null;
  const printer = typeof window !== 'undefined' ? window.CarPoolBadgePrinter : null;
  return {
    scannerAvailable: Boolean(scanner && typeof scanner.scan === 'function'),
    printerAvailable: Boolean(printer && typeof printer.print === 'function'),
  };
}

export async function scanVisitorId() {
  const bridge = typeof window !== 'undefined' ? window.CarPoolIdScanner : null;
  if (bridge && typeof bridge.scan === 'function') {
    try {
      const fields = readScanFields(await bridge.scan());
      if (!fields.fullName && !fields.imageDataUrl && !fields.documentToken) {
        return {
          available: true,
          scanned: false,
          ...fields,
          message: 'The scanner did not return a name or ID image. Enter the visitor manually.',
        };
      }
      return {
        available: true,
        scanned: true,
        ...fields,
        message: '',
      };
    } catch (error) {
      return {
        available: true,
        scanned: false,
        fullName: '',
        documentToken: null,
        imageDataUrl: null,
        message: error?.message || 'The ID scanner could not read this card. Enter the visitor manually.',
      };
    }
  }

  if (idScannerIsReady()) return scanPreparedId();

  return {
    available: false,
    scanned: false,
    fullName: '',
    documentToken: null,
    imageDataUrl: null,
    message: SCANNER_UNAVAILABLE_MESSAGE,
  };
}

export async function printVisitorBadge(badge) {
  const bridge = typeof window !== 'undefined' ? window.CarPoolBadgePrinter : null;
  if (!bridge || typeof bridge.print !== 'function') {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
      return {
        available: false,
        accepted: false,
        confirmed: false,
        message: 'Badge printer is not connected. The visit is saved.',
      };
    }
    return printDymoVisitorBadge(badge);
  }

  try {
    const result = await bridge.print({ ...badge });
    const confirmed = result?.confirmed === true;
    const accepted = confirmed || result?.accepted === true;
    let message = result?.message;
    if (!message) {
      if (confirmed) message = 'The printer confirmed the badge was printed.';
      else if (accepted) message = 'Print request accepted. Physical printing was not confirmed.';
      else message = 'The printer did not accept the badge.';
    }
    return { available: true, accepted, confirmed, message };
  } catch (error) {
    return {
      available: true,
      accepted: false,
      confirmed: false,
      message: error?.message || 'The printer did not accept the badge.',
    };
  }
}

export function dataUrlToBlob(dataUrl) {
  const [header, data] = String(dataUrl || '').split(',');
  const mime = header?.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(data || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mime });
}
